import { round } from 'lodash';
import moment from 'moment';
import { intTimeToMoment } from 'src/utils';

/**
 *
 * @param {String} time1 time in HH:mm format
 * @param {String} time2 time in HH:mm format
 * @returns {Number} time in HHmm format
 */
function getMiddleOfTwoTimes(time1, time2) {
  const m_time1 = moment(time1, 'HH:mm');
  const m_time2 = moment(time2, 'HH:mm');

  if (m_time2.isSameOrBefore(m_time1)) m_time2.add(1, 'day');

  const diff = m_time2.diff(m_time1, 'minutes');
  m_time1.add(round(diff / 4), 'minutes');

  return parseInt(m_time1.format('HHmm'));
}

/**
 *
 * @param {Object} param
 * @param {Array<Number>} param.logs
 * @param {Object} param.workHours
 * @param {string} param.workHours.am_in
 * @param {string} param.workHours.am_out
 * @param {string} param.workHours.pm_in
 * @param {string} param.workHours.pm_out
 * @returns {[Number, Number, Number, Number]}
 */
function generateCorrectLogsNoNoonLogs({ logs = [], workHours }) {
  const { am_in, pm_out } = workHours;

  const len = logs.length;
  const res = [logs?.[0] || 0, logs?.[1] || 0, logs?.[2] || 0, logs?.[3] || 0];

  const mid = getMiddleOfTwoTimes(am_in, pm_out);

  if (len === 2) {
    res[3] = res[1];
    res[1] = 0;
  } else if (len === 1 && logs[0] > mid) {
    res[3] = res[0];
    res[0] = 0;
  } else if (len > 2) {
    res[3] = logs[logs.length - 1];
  }

  return res;
}

/**
 * @param {Array<moment.Moment>} shifts
 * @param {Array<moment.Moment>} logs
 * @param {Array<moment.Moment | ''>} res
 * @param {Array<number>} timeInterval
 */
function matchLogsWithShift(shifts, logs, res, timeInterval, maxInterval) {
  if (shifts.every(shift => shift === '')) return;

  logs.forEach(log => {
    let nearestIndex = 0,
      minimumInterval = 2000;

    shifts.forEach((shift, index) => {
      if (!moment.isMoment(shift)) return;

      const interval = Math.abs(shift.diff(log, 'minutes'));
      if (interval <= minimumInterval) {
        nearestIndex = index;
        minimumInterval = interval;
      }
    });

    const newShifts = shifts.slice();
    newShifts[nearestIndex] = '';

    if (
      minimumInterval < timeInterval[nearestIndex] &&
      minimumInterval <= maxInterval
    ) {
      if (moment.isMoment(res[nearestIndex])) {
        const newLogs = logs.slice();
        newLogs[nearestIndex] = res[nearestIndex];

        matchLogsWithShift(newShifts, newLogs, res, timeInterval, maxInterval);
      }

      res[nearestIndex] = log;
      timeInterval[nearestIndex] = minimumInterval;
    } else {
      matchLogsWithShift(newShifts, logs, res, timeInterval, maxInterval);
    }
  });
}

/**
 * @param {object} param
 * @param {Array<Number>} param.logs
 * @param {Object} param.workHours
 * @param {string} param.workHours.am_in
 * @param {string} param.workHours.am_out
 * @param {string} param.workHours.pm_in
 * @param {string} param.workHours.pm_out
 * @param {number} param.maxInterval
 * @param {boolean} param.noNoonLogs
 * @returns {[Number, Number, Number, Number]}
 */
function arrangeLogs({
  workHours,
  logs,
  maxInterval = 240,
  noNoonLogs = false
}) {
  if (noNoonLogs) return generateCorrectLogsNoNoonLogs({ logs, workHours });

  const shifts = [
    moment(workHours.am_in, 'HH:mm'),
    moment(workHours.am_out, 'HH:mm'),
    moment(workHours.pm_in, 'HH:mm'),
    moment(workHours.pm_out, 'HH:mm')
  ];
  const m_logs = logs.map(log => intTimeToMoment(log));

  const res = Array.from({ length: shifts.length }, () => '');
  const timeInterval = Array.from({ length: shifts.length }, () => 2000);

  matchLogsWithShift(shifts, m_logs, res, timeInterval, maxInterval);

  return res.map(time =>
    moment.isMoment(time) ? parseInt(time.format('HHmm')) : 0
  );
}

export default arrangeLogs;
